import { programLabel } from '@/lib/programs'
/** Clickable task rows. The whole row is the link. Devs see the expert's picture on the left. */
import { Link } from 'react-router-dom'
import { StageChip } from './StageBadge'
import { BuilderAttention } from './BuilderAttention'
import { hasBuilderQuestion, needsBuilderReply, needsScienceAnswer, needsScienceDeveloper, taskDisplayStage, workflowAgentState } from '@/lib/stages'
import { fmtAgo } from '@/lib/format'
import { useSmeDirectory } from '@/lib/smes'
import { Avatar, ProfileHover } from './Avatar'
import type { Role, Task } from '@/lib/types'
import { sourceTemplate, templateLabel } from '@/lib/scientific'
import { Icon } from './Icon'

interface Props { tasks: Task[]; role: Role; empty: string; showTodo?: boolean; taskHref?: (task: Task) => string
  /** Lists inside one template omit the template each science workflow came from. */
  hideSource?: boolean }

export function TaskRows(props: Props) {
  return props.role === 'engineer' ? <EngRows {...props} /> : <Rows {...props} />
}

/** Engineer variant: loads the expert directory so each row can show the expert's picture. */
function EngRows(props: Props) {
  const smes = useSmeDirectory()
  return <Rows {...props} smes={smes} />
}

function Rows({ tasks, role, empty, showTodo = false, taskHref, hideSource = false, smes = {} }: Props & { smes?: ReturnType<typeof useSmeDirectory> }) {
  if (tasks.length === 0) return <p className="empty">{empty}</p>
  const base = role === 'sme' ? '/sme/tasks' : '/eng/tasks'
  return (
    <div className="rows">
      {tasks.map((t) => {
        const fb = t.last_feedback
        const showFb = role === 'sme' && fb && fb.to_stage === t.stage && fb.from_role === 'engineer'
        const expert = role === 'engineer' ? smes[t.sme_id] : undefined
        return (
          <Link key={t.task_id} to={taskHref?.(t) ?? `${base}/${t.task_id}`} className={`trow ${role === 'engineer' ? 'with-avatar' : ''}`}>
            {role === 'engineer' && (expert
              ? <ProfileHover user={expert}><Avatar avatar={expert.avatar} name={expert.name || expert.email} size={36} /></ProfileHover>
              : <Avatar name={t.sme_email} size={36} />)}
            <div>
              <div className="task-row-heading">
                <div className="title">{t.collection && <span className="template-label"><Icon name="layers" size={13} />{templateLabel(t.collection)}</span>}{t.title}{role === 'engineer' && <span className="task-id"> ({t.task_id})</span>}</div>
                {(needsBuilderReply(t) || role === 'sme' && needsScienceAnswer(t) || role === 'engineer' && needsScienceDeveloper(t)) && <BuilderAttention />}
              </div>
              {t.build_waiting && <div className="small muted">Waiting for environment support</div>}
              <div className="subline">{programLabel(t)}{!hideSource && sourceTemplate(t) && <> · from template {sourceTemplate(t)?.title ?? ''}</>}{role === 'engineer' && <> · {t.sme_email}{t.workflow_version !== 2 && (t.engineer_email ? <> · {t.engineer_email}</> : <> · <span className="err-text">unassigned</span></>)}</>}</div>
              {showTodo && !t.build_waiting && <div className="todo">{(t.kind === 'scientific_workflow' || t.collection) && t.stage === 3 ? (workflowAgentState(t) ? scienceTodo(t, role) : t.stage_info.label) : t.collection ? t.stage_info.label : needsBuilderReply(t) ? (role === 'sme' ? 'The build is paused. Answer the builder’s question to move it forward.' : 'The build is paused, waiting for the expert’s reply.') : hasBuilderQuestion(t) ? 'The expert’s update is awaiting peer review before the build resumes.' : t.stage === 1 ? 'A peer is reviewing the complete submission' : t.stage === 4 ? 'Review the package and rollout evidence' : t.stage === 3 && t.active_review_id ? (role === 'sme' ? 'Your rubric review is requested' : 'Waiting for expert review') : role === 'sme' ? ['Edit and submit your draft', 'Waiting for draft review', 'Upload and submit your execution', 'The authoring agent is building your task', 'Quality check in progress', 'Complete'][t.stage] : ['Expert is preparing the draft', 'Review the prompt and rubric', 'Waiting for the expert’s submission', 'The agent is building the package', 'Review the agent’s pull request and merge', 'Complete'][t.stage]}</div>}
              {showFb && <div className="feedback note warn"><strong>Feedback:</strong> {fb.message}</div>}
            </div>
            <div className="side">
              {t.lifecycle === 'retired' ? <span className="chip retired">Retired</span> : t.kind === 'scientific_workflow' || t.collection ? <span className="chip">{t.stage_info.label}</span> : <StageChip version={t.workflow_version} stage={taskDisplayStage(t)} />}
              <span className="when">{fmtAgo(t.updated_at)}</span>
              <span className="arrow">›</span>
            </div>
          </Link>
        )
      })}
    </div>
  )
}

/** What a science workflow in the authoring stage is waiting for, in the reader's words. */
function scienceTodo(task: Task, role: Role) {
  if (needsScienceAnswer(task)) return role === 'sme' ? 'The agent asked a question. Answer it to continue the build.' : 'Waiting for the expert to answer the agent.'
  if (needsScienceDeveloper(task)) return role === 'sme' ? 'A developer is fixing a problem with the build.' : 'The pipeline stopped. Open it to retry or restart the builder.'
  return 'The agent is building and grading the workflow. The quality check opens on its own.'
}
